/**
 * Reusable loading UI primitives — used across dashboards so every page
 * shows a consistent skeleton / spinner while data is being fetched.
 *
 * Exports:
 *  - <SkeletonBlock height/> : Full-block shimmering placeholder (e.g. chart card).
 *  - <SkeletonRibbon count/> : Row of skeleton KPI cards.
 *  - <Spinner size/>         : Rotating dot spinner.
 *  - <RefetchPill/>          : Floating chip shown while stale data is on screen.
 */
import { Loader2 } from "lucide-react";

const SHIMMER = {
  background: "linear-gradient(90deg, #eef2f7 25%, #f7f9fc 50%, #eef2f7 75%)",
  backgroundSize: "200% 100%",
  animation: "sk-shimmer 1.4s linear infinite",
  borderRadius: 8,
};

const KEYFRAMES =
  "@keyframes sk-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}";

export function SkeletonBlock({ height = 200, className = "", testId }) {
  return (
    <>
      <style>{KEYFRAMES}</style>
      <div className={`bg-white ${className}`}
        style={{ ...SHIMMER, height }}
        data-testid={testId || "skeleton-block"}/>
    </>
  );
}

export function SkeletonRibbon({ count = 4 }) {
  return (
    <>
      <style>{KEYFRAMES}</style>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6"
        data-testid="skeleton-ribbon">
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="p-4 bg-white" style={{ ...SHIMMER, height: 92 }} />
        ))}
      </div>
    </>
  );
}

export function Spinner({ size = 16, color = "#f4c400" }) {
  return <Loader2 size={size} className="animate-spin" style={{ color }}/>;
}

/**
 * Compact "Actualizando…" pill shown fixed at the bottom-right while a
 * refetch is happening on top of already-rendered data. Non-blocking so
 * the user keeps seeing the previous values instead of a jarring flash.
 */
export function RefetchPill({ label = "Actualizando…" }) {
  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex items-center gap-2 px-3 py-2 rounded-full shadow-lg"
      style={{
        background: "#111827",
        color: "#f4c400",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
      }}
      data-testid="refetch-pill">
      <Spinner size={12} color="#f4c400"/>
      {label}
    </div>
  );
}
